import { apiClient } from '@/shared/api/client';
import type {
  JobPosition,
  CreateJobPositionRequest,
  UpdateJobPositionRequest,
} from '../types/jobPosition.types';
import type {
  JobPositionCandidateWithDetails,
  AddCandidateToJobPositionRequest,
  UpdateApplicationRequest,
  ListJobPositionCandidatesParams,
} from '../types/jobPositionCandidate.types';
import type { ApiResponse } from '@/shared/types/api.types';
import type { JobPositionId, CandidateId } from '@/shared/types/branded.types';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestConfig {
  path: string;
  method: HttpMethod;
  body?: unknown;
}

interface PositionIdentifier {
  positionId: JobPositionId;
}

interface CandidateIdentifier extends PositionIdentifier {
  candidateId: CandidateId;
}

async function executeRequest<T>(config: RequestConfig): Promise<T> {
  const response = await apiClient<ApiResponse<T>>(config.path, {
    method: config.method,
    body: config.body,
  });
  return response.data;
}

function buildPath(segments: string[]): string {
  return segments.join('/');
}

function buildQueryString(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return '';
  const qs = new URLSearchParams(entries as [string, string][]).toString();
  return `?${qs}`;
}

function positionPath(id?: JobPositionId): string {
  return id ? buildPath(['/job-positions', id]) : '/job-positions';
}

function candidatePath(ids: PositionIdentifier | CandidateIdentifier): string {
  const base = buildPath(['/job-positions', ids.positionId, 'candidates']);
  return 'candidateId' in ids ? buildPath([base, ids.candidateId]) : base;
}

type CandidateMutationMethod = 'POST' | 'PATCH';

function candidateMutation(
  ids: PositionIdentifier | CandidateIdentifier,
  method: CandidateMutationMethod,
  body: unknown
): Promise<JobPositionCandidateWithDetails> {
  return executeRequest<JobPositionCandidateWithDetails>({
    path: candidatePath(ids),
    method,
    body,
  });
}

export const jobPositionService = {
  async list(): Promise<JobPosition[]> {
    return executeRequest<JobPosition[]>({ path: positionPath(), method: 'GET' });
  },

  async getById(id: JobPositionId): Promise<JobPosition> {
    return executeRequest<JobPosition>({ path: positionPath(id), method: 'GET' });
  },

  async create(body: CreateJobPositionRequest): Promise<JobPosition> {
    return executeRequest<JobPosition>({ path: positionPath(), method: 'POST', body });
  },

  async update(id: JobPositionId, body: UpdateJobPositionRequest): Promise<JobPosition> {
    return executeRequest<JobPosition>({ path: positionPath(id), method: 'PUT', body });
  },

  async remove(id: JobPositionId): Promise<void> {
    await executeRequest<void>({ path: positionPath(id), method: 'DELETE' });
  },

  async archive(id: JobPositionId): Promise<JobPosition> {
    return executeRequest<JobPosition>({ path: `${positionPath(id)}/archive`, method: 'PATCH' });
  },

  async unarchive(id: JobPositionId): Promise<JobPosition> {
    return executeRequest<JobPosition>({ path: `${positionPath(id)}/unarchive`, method: 'PATCH' });
  },

  async getSuggestions(params: { field: string; query: string }): Promise<string[]> {
    const qs = buildQueryString(params);
    return executeRequest<string[]>({
      path: `/job-positions/suggestions${qs}`,
      method: 'GET',
    });
  },

  // Candidate operations
  async listCandidates(
    ids: PositionIdentifier,
    params?: ListJobPositionCandidatesParams
  ): Promise<JobPositionCandidateWithDetails[]> {
    const qs = buildQueryString({ status: params?.status });
    return executeRequest<JobPositionCandidateWithDetails[]>({
      path: `${candidatePath(ids)}${qs}`,
      method: 'GET',
    });
  },

  async addCandidate(
    ids: PositionIdentifier,
    body: AddCandidateToJobPositionRequest
  ): Promise<JobPositionCandidateWithDetails> {
    return candidateMutation(ids, 'POST', body);
  },

  async updateCandidate(
    ids: CandidateIdentifier,
    body: UpdateApplicationRequest
  ): Promise<JobPositionCandidateWithDetails> {
    return candidateMutation(ids, 'PATCH', body);
  },

  async removeCandidate(ids: CandidateIdentifier): Promise<void> {
    await executeRequest<void>({ path: candidatePath(ids), method: 'DELETE' });
  },
};
